<template>
  <!-- Table Container Card -->
  <b-card id="page-permissions" no-body class="mb-0 p-2">
    <b-card-header class="pl-0 pr-0">
      <b-card-title>
        <h2>{{ $t(label("title")) }}</h2>
      </b-card-title>

      <b-col cols="12" md="8" class="d-none d-md-table">
        <b-col cols="12" class="d-flex align-items-between justify-content-end">
          <b-button
            variant="outline-primary"
            class="mr-1"
            @click="openCopyPermissionsModal"
          >
            <feather-icon icon="CopyIcon" class="mr-25" />
            <span class="text-nowrap">{{ $t(copyLabel("action")) }}</span>
          </b-button>
          <b-button id="tooltip-create" variant="primary" @click="onCreate">
            <feather-icon icon="PlusIcon" class="mr-25" />
            <span class="text-nowrap">{{ $t(label("register")) }}</span>
          </b-button>
        </b-col>
      </b-col>

      <b-col
        cols="12"
        sm="12"
        md="8"
        class="d-md-none d-flex justify-content-end align-items-center pt-2"
      >
        <b-button
          variant="outline-primary"
          class="btn-icon mr-1"
          :aria-label="$t(copyLabel('action'))"
          @click="openCopyPermissionsModal"
        >
          <feather-icon icon="CopyIcon" size="18" />
        </b-button>
        <b-button
          id="tooltip-create"
          variant="primary"
          @click="onCreate"
          class="btn-icon"
        >
          <feather-icon icon="PlusIcon" size="18" />
        </b-button>
      </b-col>

      <b-tooltip
        placement="top"
        target="tooltip-create"
        triggers="hover"
        @click="onInfo"
      >
        <strong>
          <feather-icon icon="ExternalLinkIcon" class="mr-25 mt-0" />
          Ver tutorial
        </strong>
        <span> </span>
      </b-tooltip>
    </b-card-header>

    <!-- Table for larger devices -->
    <b-table
      id="permissions-table"
      responsive
      ref="permissions-table"
      :head-variant="headVariant"
      :striped="striped"
      :bordered="bordered"
      :borderless="borderless"
      :outlined="outlined"
      :per-page="perPage"
      :busy="loading"
      :items="items"
      :fields="fields"
      class="d-none d-md-table shadow-sm !rounded"
    >
      <template #head()="data">
        <span class="text-black">{{
          $t(labelHeader(data.label.toLowerCase()))
        }}</span>
      </template>

      <template #cell(name)="data">
        <slot name="cell-name" :data="data">
          {{ data.item.name || "N/A" }}
        </slot>
      </template>

      <template #cell(actions)="data">
        <feather-icon
          class="text-success mr-25 cursor-pointer"
          aria-label="Show details"
          size="18"
          icon="FileTextIcon"
          @click="onShow(data.item.guid, data.item)"
        />

        <feather-icon
          class="text-primary mr-25 cursor-pointer"
          aria-label="Edit details"
          size="18"
          icon="EditIcon"
          @click="onEdit(data.item.guid, data.item)"
        />

        <feather-icon
          class="text-danger cursor-pointer"
          aria-label="Delete details"
          size="18"
          icon="Trash2Icon"
          @click="onDelete(data.item.idPermission)"
        />
      </template>

      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle ml-50"></b-spinner>
          <strong>{{ $t("loading") }}...</strong>
        </div>
      </template>
    </b-table>

    <!-- List for mobile devices -->
    <div id="list-container" class="d-block d-md-none mt-1 list-container">
      <b-list-group>
        <b-list-group-item
          v-for="item in items"
          :key="item.guid"
          class="flex-column align-items-start border border-primary rounded mb-1"
        >
          <div class="d-flex w-100 justify-content-between m-0 p-0">
            <p style="margin-bottom: 0 !important">
              <strong> {{ $t("name") }} : {{ item.name || "N/A" }}</strong>
            </p>

            <b-dropdown
              variant="link"
              no-caret
              class="chart-dropdown"
              toggle-class="p-0"
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="18"
                  class="text-body cursor-pointer"
                />
              </template>
              <b-dropdown-item>
                <div class="d-flex align-items-center justify-content-between">
                  {{ $t("show") }}
                  <feather-icon
                    class="text-success mr-25"
                    size="18"
                    icon="FileTextIcon"
                    @click="onShow(item.guid, item)"
                  />
                </div>
              </b-dropdown-item>
              <b-dropdown-item>
                <div class="d-flex align-items-center justify-content-between">
                  {{ $t("edit") }}
                  <feather-icon
                    class="text-primary mr-25"
                    size="18"
                    icon="EditIcon"
                    @click="onEdit(item.guid, item)"
                  />
                </div>
              </b-dropdown-item>
              <b-dropdown-item @click="onDelete(item.idPermission)">
                <div class="d-flex align-items-center justify-content-between">
                  <feather-icon
                    class="text-danger cursor-pointer"
                    aria-label="Delete details"
                    size="18"
                    icon="Trash2Icon"
                  />
                  {{ $t("delete") }}
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-list-group-item>
        <b-list-group-item v-if="items.length == 0" class="text-center">
          {{ $t("notRegister") }}
        </b-list-group-item>
        <b-list-group-item v-if="loading">
          <b-spinner variant="success"></b-spinner>
        </b-list-group-item>
      </b-list-group>
    </div>
    <!-- pagination -->
    <!-- <div class="mx-2 d-none d-md-table">
      <b-row>
        <b-col cols="12" md="2" class="mb-2">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">{{
              $t("perPage")
            }}</label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="pageOptions"
              @change="handlePerPageChange"
              class="w-50"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="10"
          class="d-flex align-items-center justify-content-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            pills
            @change="handlePageChange"
            first-number
            last-number
          >
            <template #prev-text>
              <feather-icon size="18" icon="ChevronLeftIcon" />
            </template>
            <template #next-text>
              <feather-icon size="18" icon="ChevronRightIcon" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div> -->

    <b-modal
      v-model="showCopyPermissionsModal"
      centered
      hide-footer
      no-close-on-backdrop
      :title="$t(copyLabel('title'))"
      @hidden="resetCopyPermissionsForm"
    >
      <p class="text-muted mb-2">
        {{ $t(copyLabel("description")) }}
      </p>

      <b-form-group
        :label="$t(copyLabel('sourceCompany'))"
        label-for="source-company"
      >
        <v-select
          input-id="source-company"
          v-model="copyPermissionForm.guidCompanyOrigen"
          :options="companies"
          label="name"
          :reduce="(company) => company.guid"
          :placeholder="$t(copyLabel('selectSourceCompany'))"
          :loading="loadingCompanies"
          :disabled="loadingCompanies || copyingPermissions"
        >
          <span slot="no-options">{{ $t("sorryOption") }}</span>
        </v-select>
      </b-form-group>

      <b-form-group
        :label="$t(copyLabel('destinationCompany'))"
        label-for="destination-company"
      >
        <v-select
          input-id="destination-company"
          v-model="copyPermissionForm.guidCompanyDestino"
          :options="destinationCompanies"
          label="name"
          :reduce="(company) => company.guid"
          :placeholder="$t(copyLabel('selectDestinationCompany'))"
          :loading="loadingCompanies"
          :disabled="
            loadingCompanies ||
            copyingPermissions ||
            !copyPermissionForm.guidCompanyOrigen
          "
        >
          <span slot="no-options">{{ $t("sorryOption") }}</span>
        </v-select>
      </b-form-group>

      <b-alert v-if="loadingCompanies" show variant="info" class="mb-2">
        {{ $t(copyLabel("loadingCompanies")) }}
      </b-alert>

      <b-alert
        v-else-if="!loadingCompanies && companies.length === 0"
        show
        variant="warning"
        class="mb-2"
      >
        {{ $t(copyLabel("noCompanies")) }}
      </b-alert>

      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="copyingPermissions"
          @click="showCopyPermissionsModal = false"
        >
          {{ $t("cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="!canCopyPermissions"
          @click="onCopyPermissions"
        >
          <b-spinner v-if="copyingPermissions" small class="mr-50" />
          {{ $t(copyLabel("confirmAction")) }}
        </b-button>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import _ from "lodash";
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BSpinner,
  VBTooltip,
  BTooltip,
  BFormSelect,
  BAvatar,
  BFormGroup,
  BListGroup,
  BListGroupItem,
  BDropdown,
  BDropdownItem,
  BModal,
  BAlert,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import {
  allPermissions,
  permissionDelete,
  copyPermission,
} from "@core/api/request/permissions";
import { getParams, showLoading, stopLoading } from "@core/utils/utils";
import { allCompanies } from "@core/api/request/company";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BSpinner,
    BFormSelect,
    BFormGroup,
    BTooltip,
    BAvatar,
    vSelect,
    BListGroup,
    BListGroupItem,
    BDropdown,
    BDropdownItem,
    BModal,
    BAlert,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data: () => ({
    loading: false,
    companyGuid: null,
    permissionsRequestId: 0,
    totalRows: 0,
    currentPage: 1,
    perPage: 10,
    pageOptions: [10, 50, 100],
    hasNextPage: true,
    items: [],
    headVariant: "light",
    striped: false,
    outlined: false,
    bordered: true,
    borderless: false,
    items: [],
    fields: [
      { label: "name", key: "name", sortable: true },
      { label: "action", key: "actions", sortable: false },
    ],
    companies: [],
    loadingCompanies: false,
    copyingPermissions: false,
    showCopyPermissionsModal: false,
    copyPermissionForm: {
      guidCompanyOrigen: null,
      guidCompanyDestino: null,
    },
  }),
  computed: {
    destinationCompanies() {
      return this.companies.filter(
        (company) => company.guid !== this.copyPermissionForm.guidCompanyOrigen,
      );
    },
    canCopyPermissions() {
      const { guidCompanyOrigen, guidCompanyDestino } = this.copyPermissionForm;
      return (
        !this.loadingCompanies &&
        !this.copyingPermissions &&
        !!guidCompanyOrigen &&
        !!guidCompanyDestino &&
        guidCompanyOrigen !== guidCompanyDestino
      );
    },
  },
  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      this.companyGuid = guid || null;
      this.currentPage = 1;
      this.hasNextPage = true;
      this.items = [];
      this.totalRows = 0;
      this.loadPermissions();
    },
    "copyPermissionForm.guidCompanyOrigen"(guidCompanyOrigen) {
      if (this.copyPermissionForm.guidCompanyDestino === guidCompanyOrigen) {
        this.copyPermissionForm.guidCompanyDestino = null;
      }
    },
  },
  created() {
    this.companyGuid = this.$store.state.appConfig.activeCompanyGuid || null;
    this.loadPermissions();
  },
  async mounted() {
    const listContainer = document.getElementById("list-container");
    if (listContainer) {
      listContainer.addEventListener("scroll", this.handleScroll);
    }
  },
  beforeDestroy() {
    const listContainer = document.getElementById("list-container");
    if (listContainer) {
      listContainer.removeEventListener("scroll", this.handleScroll);
    }
  },
  methods: {
    async loadPermissions() {
      const requestId = ++this.permissionsRequestId;
      this.loading = true;

      try {
        const param = this.chargeParams(1);
        const resp = await allPermissions(param);

        if (requestId !== this.permissionsRequestId) return;

        const data = _.get(resp, "data");
        const permissions = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : [];

        this.items = permissions;
        this.perPage = permissions.length || 10;
        this.totalRows = data?.totalRows || permissions.length;
        this.currentPage = data?.currentPage || 1;
        this.hasNextPage = Array.isArray(data)
          ? false
          : data?.currentPage < data?.totalPages;
      } catch (error) {
        if (requestId !== this.permissionsRequestId) return;

        this.alertToast(
          "danger",
          "Error",
          error.message,
          "AlertTriangleIcon",
        );
      } finally {
        if (requestId === this.permissionsRequestId) {
          this.loading = false;
        }
      }
    },

    async loadPermissionsInfinity() {
      if (!this.hasNextPage || this.loading) return;

      const requestId = ++this.permissionsRequestId;
      this.loading = true;

      try {
        const param = this.chargeParams(this.currentPage);
        const resp = await allPermissions(param);

        if (requestId !== this.permissionsRequestId) return;

        const data = _.get(resp, "data");
        const permissions = Array.isArray(data?.items) ? data.items : [];

        this.items = [...this.items, ...permissions];
        this.perPage = this.items.length || 10;
        this.totalRows = data?.totalRows || this.items.length;
        this.hasNextPage = data?.currentPage < data?.totalPages;
      } catch (error) {
        if (requestId !== this.permissionsRequestId) return;

        this.alertToast(
          "danger",
          "Error",
          error.message,
          "AlertTriangleIcon",
        );
      } finally {
        if (requestId === this.permissionsRequestId) {
          this.loading = false;
        }
      }
    },

    async handleScroll() {
      const container = document.getElementById("list-container");
      if (
        !this.loading &&
        this.hasNextPage &&
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 1
      ) {
        this.currentPage++;
        await this.loadPermissionsInfinity();
      }
    },
    label(field) {
      return `pages.permissions.table.${field}`;
    },

    labelHeader(field) {
      return `pages.permissions.table.tableHeader.${field}`;
    },
    copyLabel(field) {
      return `pages.permissions.copy.${field}`;
    },
    chargeParams(page = this.currentPage) {
      const arrParams = [];

      arrParams.push(`page,${page}`);
      arrParams.push(`rows,100`);

      if (this.companyGuid) {
        arrParams.push(`guidCompany,${this.companyGuid}`);
      }

      return getParams(arrParams);
    },
    handlePageChange(value) {
      this.currentPage = value;
      this.hasNextPage = true;
      this.loadPermissions();
    },

    handlePerPageChange() {
      this.loadPermissions();
    },

    async loadCompanies() {
      this.loadingCompanies = true;
      try {
        const response = await allCompanies(getParams(["page,1", "rows,999"]));
        const data = _.get(response, "data");
        this.companies = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        this.companies = [];
        this.alertToast(
          "danger",
          this.$t("Error"),
          error.message,
          "AlertTriangleIcon",
        );
      } finally {
        this.loadingCompanies = false;
      }
    },

    openCopyPermissionsModal() {
      this.resetCopyPermissionsForm();
      this.showCopyPermissionsModal = true;
      if (this.companies.length === 0 && !this.loadingCompanies) {
        this.loadCompanies();
      }
    },

    resetCopyPermissionsForm() {
      this.copyPermissionForm.guidCompanyOrigen = null;
      this.copyPermissionForm.guidCompanyDestino = null;
    },

    async onCopyPermissions() {
      if (!this.canCopyPermissions) {
        this.alertToast(
          "warning",
          this.$t("Alert"),
          this.$t(this.copyLabel("validation")),
          "AlertTriangleIcon",
        );
        return;
      }

      const sourceCompany = this.companies.find(
        (company) => company.guid === this.copyPermissionForm.guidCompanyOrigen,
      );
      const destinationCompany = this.companies.find(
        (company) =>
          company.guid === this.copyPermissionForm.guidCompanyDestino,
      );
      const result = await this.$swal({
        title: this.$t(this.copyLabel("confirmTitle")),
        text: this.$t(this.copyLabel("confirmMessage"), {
          source: sourceCompany?.name || "",
          destination: destinationCompany?.name || "",
        }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t(this.copyLabel("confirmAction")),
        cancelButtonText: this.$t("cancel"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-secondary ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.value) return;

      this.copyingPermissions = true;
      try {
        await copyPermission({
          guidCompanyOrigen: this.copyPermissionForm.guidCompanyOrigen,
          guidCompanyDestino: this.copyPermissionForm.guidCompanyDestino,
        });
        this.showCopyPermissionsModal = false;
        this.alertToast(
          "success",
          this.$t(this.copyLabel("successTitle")),
          this.$t(this.copyLabel("successMessage")),
          "CheckCircleIcon",
        );
        await this.loadPermissions();
      } catch (error) {
        this.alertToast(
          "danger",
          this.$t("Error"),
          error.message,
          "AlertTriangleIcon",
        );
      } finally {
        this.copyingPermissions = false;
      }
    },

    onCreate() {
      this.$router.push({
        name: "permissionCreate",
        params: {
          option: 1, // option 1 to create
        },
      });
    },

    onShow(guid, data) {
      this.$router.push({
        name: "permissionEdit",
        params: {
          option: 2, // option 2 to show
          guid: guid,
          data: JSON.stringify(data),
        },
      });
    },

    onEdit(guid, data) {
      this.$router.push({
        name: "permissionEdit",
        params: {
          option: 3, // option 3 to edit
          guid: guid,
          data: JSON.stringify(data),
        },
      });
    },

    onDelete(idPermission) {
      this.$swal({
        title: this.$t("areYouSure"),
        text: this.$t("willNotBeAble"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("yesDelete"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          showLoading();
          permissionDelete(idPermission)
            .then(async (resp) => {
              this.$swal({
                icon: "success",
                title: this.$t("Deleted"),
                text: this.$t("beenDeleted"),
                customClass: {
                  confirmButton: "btn btn-success",
                },
              });
              await this.loadPermissions();
            })
            .catch((error) => {
              console.error("error", error.message);
              this.alertToast(
                "danger",
                this.$t("codeAlert"),
                error.message,
                "AlertTriangleIcon",
              );
            })
            .finally(() => {
              stopLoading();
            });
        } else if (result.dismiss === "cancel") {
          this.message(
            this.$t("canceled"),
            this.$t("registerDeletedCanceled"),
            "error",
          );
        }
      });
    },

    onInfo() {},

    message(title, text, icon, html) {
      this.$swal({
        title,
        text,
        html,
        icon,
        customClass: {
          confirmButton: "btn btn-success",
        },
      });
    },
    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.list-container {
  height: 60vh !important; /* Ajusta el alto según tus necesidades */
  overflow-y: auto;
}
</style>
